import {
  PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getExpandedRowModel, getSortedRowModel,
  SortingState, useReactTable,
} from "@tanstack/react-table";
import DivLoader from "components/divloader/DivLoader";
import {  useLocation } from "react-router-dom";
import React, { useState, useEffect, useContext } from "react";
import { PaymentRowObj } from "..";
import InfoModal from "./InfoModal";
import AdvancedInfoModal from "./AdvancedInfoModal";
import RefundModal from "./RefundModal";
import { convertToFloat } from "utils/formatNumber";
import { BsArrowDown, BsArrowUp, BsReceipt, BsThreeDotsVertical } from "react-icons/bs";
import { ClientContext } from "ClientProvider";
import { DownloadCSV } from "api/payments";
import { toast } from "react-hot-toast";
import FilterModal from "./FilterModal";
import { FaDownload } from "react-icons/fa";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import Searchbox from "components/fields/Searchbox";
import { getImage } from "utils/commonFunction";
import Pagination from "components/pagination";
import StatusRender from "./StatusRender";
import RetryModal from "./RetryModal";
import ChangeStatusModal from "./StatusChangeModal";
import { Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow,Menu,MenuButton,MenuList,MenuItem,IconButton } from "@chakra-ui/react";
import CustomToolTip from "components/tooltips/new";
import { RiArrowUpDownFill } from "react-icons/ri";
import visa from "assets/svg/card_type/visa.svg";
import MasterCard from "assets/svg/card_type/mastercard.svg";
import Discover from "assets/svg/card_type/discover.svg";
import Amex from "assets/svg/card_type/amex.svg";
import MaestroCard from "assets/svg/card_type/maestro.svg";
import DinersClub from "assets/svg/card_type/dinersclub.svg";
import JCB from "assets/svg/card_type/jcb.svg";
import UnionPay from "assets/svg/card_type/unionpay.svg";
import TripleToggleSwitch from "./TripleToggleSwitch";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import { FiActivity, FiCreditCard, FiFileText, FiInfo, FiLayers, FiServer, FiUpload, FiUser,FiCopy, FiCheck} from "react-icons/fi";

const columnHelper = createColumnHelper<PaymentRowObj>();

const CARD_SVGS: Record<string, string> = {
  VISA: visa, MasterCard, Discover, Amex, MaestroCard, DinersClub, JCB, UnionPay,
};

const getCardSVG = (type: string) => {
  const src = CARD_SVGS[type];
  if (!src) return <span className="txn-muted">—</span>;
  return <img style={{ maxWidth: "56px", height: "auto" }} src={src} alt={type} title={type} />;
};

const CopyableUrl = ({ value }: { value?: string }) => {
  const [copied, setCopied] = React.useState(false);

  if (!value) {
    return <span className="txn-url-empty">—</span>;
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch (error) {
      console.error("Failed to copy URL:", error);
    }
  };

  return (
    <div className="txn-copy-url">
      <span
        className="txn-copy-url-text"
        title={value}
      >
        {value}
      </span>

      <button
        type="button"
        className={`txn-copy-url-btn ${copied ? "copied" : ""}`}
        onClick={handleCopy}
        aria-label={copied ? "URL copied" : "Copy URL"}
      >
        {copied ? (
          <FiCheck size={12} />
        ) : (
          <FiCopy size={12} />
        )}
      </button>
    </div>
  );
};

const parseJsonValue = (value: any) => {
  if (!value) return null;

  if (typeof value === "object") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const formatLabel = (key: string) => {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatDisplayValue = (value: any, key = "") => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (key.toLowerCase().includes("cvv")) {
    return "•••";
  }

  if (
    key.toLowerCase().includes("cardnumber") ||
    key.toLowerCase().includes("card_number")
  ) {
    const stringValue = String(value);

    if (stringValue.length > 4) {
      return `•••• ${stringValue.slice(-4)}`;
    }

    return "••••";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const GatewayDataRenderer = ({
  data,
  parentKey = "",
}: {
  data: any;
  parentKey?: string;
}) => {
  if (!data) {
    return (
      <div className="txn-json-empty">
        No data available
      </div>
    );
  }

  if (typeof data !== "object") {
    return (
      <div className="txn-json-value">
        {formatDisplayValue(data, parentKey)}
      </div>
    );
  }

  return (
    <div className="txn-json-list">
      {Object.entries(data).map(([key, value]) => {
        const isObject =
          typeof value === "object" &&
          value !== null &&
          !Array.isArray(value);

        return (
          <div
            className={`txn-json-item ${
              isObject ? "txn-json-item--nested" : ""
            }`}
            key={key}
          >
            <span className="txn-json-label">
              {formatLabel(key)}
            </span>

            {isObject ? (
              <div className="txn-json-nested">
                <GatewayDataRenderer
                  data={value}
                  parentKey={key}
                />
              </div>
            ) : (
              <span
                className="txn-json-value"
                title={formatDisplayValue(value, key)}
              >
                {formatDisplayValue(value, key)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

const GatewayResponseSection = ({
  data,
}: {
  data: any;
}) => {
  if (!data || typeof data !== "object") return null;

  const sections = [
    {
      key: "requested_data",
      title: "Requested Data",
      subtitle: "Original payment request",
      icon: <FiFileText size={13} />,
      className: "requested",
    },
    {
      key: "input_data",
      title: "Input Data",
      subtitle: "Gateway request sent",
      icon: <FiUpload size={13} />,
      className: "input",
    },
    {
      key: "gateway_response",
      title: "Gateway Response",
      subtitle: "Gateway result",
      icon: <FiActivity size={13} />,
      className: "response",
    }
  ];

  return (
    <div className="txn-gateway-section">

      <div className="txn-section-header">
        <div className="txn-section-heading">
          <div className="txn-section-icon txn-section-icon--green">
            <FiServer size={15} />
          </div>

          <div>
            <div className="txn-section-title">
              Gateway Communication
            </div>

            <div className="txn-section-subtitle">
              Request and response information exchanged with the gateway
            </div>
          </div>
        </div>
      </div>

      <div className="txn-gateway-flow">

        {sections.map((section, index) => {
          const sectionData = data?.[section.key];

          return (
            <React.Fragment key={section.key}>

              <div
                className={`txn-gateway-card txn-gateway-card--${section.className}`}
              >
                <div className="txn-gateway-card-header">
                  <div className="txn-gateway-card-title">
                    <span className="txn-gateway-card-icon">
                      {section.icon}
                    </span>

                    <div>
                      <strong>{section.title}</strong>
                      <span>{section.subtitle}</span>
                    </div>
                  </div>

                  <span className="txn-gateway-card-number">
                    0{index + 1}
                  </span>
                </div>

                <div className="txn-gateway-card-body">
                  <GatewayDataRenderer
                    data={sectionData}
                    parentKey={section.key}
                  />
                </div>
              </div>

              {index < sections.length - 1 && (
                <div className="txn-history-arrow">
                  <span>→</span>
                </div>
              )}

            </React.Fragment>
          );
        })}

      </div>
    </div>
  );
};

const DynamicPaymentFields = ({
  data,
  excludedKeys = [],
}: {
  data: any;
  excludedKeys?: string[];
}) => {
  if (!data || typeof data !== "object") return null;

  const entries = Object.entries(data).filter(
    ([key, value]) =>
      !excludedKeys.includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== ""
  );

  if (!entries.length) return null;

  return (
    <div className="txn-info-subsection">
      <div className="txn-info-subsection-title">
        <FiLayers size={13} />
        Additional Information
      </div>

      <div className="txn-dynamic-grid">
        {entries.map(([key, value]) => (
          <div className="txn-dynamic-field" key={key}>
            <span>{formatLabel(key)}</span>

            <strong title={formatDisplayValue(value, key)}>
              {formatDisplayValue(value, key)}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const PaymentInformationSection = ({
  paymentResponse,
  gatewayResponse,
  roleData
}: {
  paymentResponse?: any;
  gatewayResponse?: any;
  roleData:any
}) => {
  const payment = React.useMemo(() => parseJsonValue(paymentResponse) || {}, [paymentResponse]);
  const gateway = React.useMemo(() => parseJsonValue(gatewayResponse) || {}, [gatewayResponse]);

  const binInfo = payment?.binInformation || {};
  const description = payment?.description || {};
  const customerInfo = payment?.customer_information || {};

  const commonPaymentFields = [
    {
      label: "Amount",
      value:
        payment?.amount && payment?.currency
          ? `${payment.amount} ${payment.currency}`
          : payment?.amount || "—",
      type: "amount",
    },
    {
      label: "Status",
      value: payment?.status || "—",
      type: "status",
    },
    {
      label: "Payment Type",
      value: payment?.payment_type || "—",
    },
    {
      label: "Currency",
      value: payment?.currency || "—",
    },
    {
      label: "Descriptor",
      value: payment?.Descriptor || payment?.descriptor || "—",
    },
    {
      label: "Request Date",
      value: payment?.request_date || "—",
    },
    {
      label: "Error/Status Code",
      value: payment?.error_code || "—",
    },
    {
      label: "Transaction ID",
      value: description?.transaction_id || payment?.id || "—",
      copy: true,
    },
  ];

  return (
    <div className="txn-payment-info-section">

      {/* HEADER */}
      <div className="txn-section-header">
        <div className="txn-section-heading">
          <div className="txn-section-icon txn-section-icon--blue">
            <FiCreditCard size={15} />
          </div>

          <div>
            <div className="txn-section-title">
              Payment Information
            </div>

            <div className="txn-section-subtitle">
              Transaction, payment method and customer details
            </div>
          </div>
        </div>
      </div>

      {/* COMMON PAYMENT INFORMATION */}
      <div className="txn-payment-summary-grid">
        {commonPaymentFields.map((item) => (
          <div className="txn-payment-field" key={item.label}>
            <span className="txn-payment-field-label">
              {item.label}
            </span>

            {item.type === "status" ? (
              <span
                className={`txn-payment-status ${String(
                  item.value
                ).toLowerCase()}`}
              >
                <span className="txn-payment-status-dot" />
                {item.value}
              </span>
            ) : (
              <span
                className={`txn-payment-field-value ${
                  item.type === "amount"
                    ? "txn-payment-amount"
                    : ""
                }`}
              >
                {item.value}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* CARD INFORMATION */}
      {(binInfo?.card_type ||
        binInfo?.lastFour ||
        binInfo?.bin) && (
        <div className="txn-info-subsection">
          <div className="txn-info-subsection-title">
            <FiCreditCard size={13} />
            Card Information
          </div>

          <div className="txn-info-card-grid">
            <div className="txn-info-card">
              <span>Card Type</span>
              <strong>{binInfo?.card_type || "—"}</strong>
            </div>

            <div className="txn-info-card">
              <span>Card Number</span>
              <strong>
                {binInfo?.lastFour
                  ? `•••• ${binInfo.lastFour}`
                  : "—"}
              </strong>
            </div>

            <div className="txn-info-card">
              <span>BIN</span>
              <strong>{binInfo?.bin || "—"}</strong>
            </div>
          </div>
        </div>
      )}

      {/* CUSTOMER INFORMATION */}
      {(customerInfo?.customer_id ||
        customerInfo?.payment_method_id) && (
        <div className="txn-info-subsection">
          <div className="txn-info-subsection-title">
            <FiUser size={13} />
            Customer & Payment Method
          </div>

          <div className="txn-info-card-grid">
            <div className="txn-info-card txn-info-card--wide">
              <span>Customer ID</span>
              <strong title={customerInfo?.customer_id}>
                {customerInfo?.customer_id || "—"}
              </strong>
            </div>

            <div className="txn-info-card txn-info-card--wide">
              <span>Payment Method ID</span>
              <strong title={customerInfo?.payment_method_id}>
                {customerInfo?.payment_method_id || "—"}
              </strong>
            </div>
          </div>
        </div>
      )}

      {(description?.message ||
        description?.next_action ||
        description?.return_url ||
        description?.callback_url) && (
        <div className="txn-info-subsection">
          <div className="txn-info-subsection-title">
            <FiInfo size={13} />
            Payment Details
          </div>

          <div className="txn-description-grid">

            {description?.message && (
              <div className="txn-description-card">
                <span>Message</span>
                <strong>{description.message}</strong>
              </div>
            )}

            {description?.next_action && (
              <div className="txn-description-card">
                <span>Next Action</span>
                <strong>{description.next_action}</strong>
              </div>
            )}

            {description?.return_url && (
              <div className="txn-description-card ">
                <span>Return URL</span>

                <CopyableUrl value={description.return_url} />
              </div>
            )}

            {description?.callback_url && (
              <div className="txn-description-card ">
                <span>Callback URL</span>

                <CopyableUrl value={description.callback_url} />
              </div>
            )}

          </div>
        </div>
      )}

      
      <DynamicPaymentFields
        data={payment}
        excludedKeys={[
          "id",
          "amount",
          "status",
          "currency",
          "binInformation",
          "description",
          "error_code",
          "Descriptor",
          "descriptor",
          "receipt_url",
          "qr_code",
          "request_date",
          "payment_type",
          "new_currency",
          "new_amount",
          "customer_information",
        ]}
      />

      {roleData?.[0]?.payment?.value?.view_gateway_response_information && (
        <GatewayResponseSection data={gateway} />
      )}

    </div>
  );
};

const TransactionDetailsRow = React.memo(
  ({ row, isExpanded,roleData }: { row: any; isExpanded: boolean;roleData:any }) => {
    if (!isExpanded) return null;

    return (
      <tr className="txn-details-tr">
        <td
          colSpan={row.getVisibleCells().length}
          className="txn-details-td"
        >
          <div className="txn-details-panel">
            <div className="txn-details-header">
              <div>
                <div className="txn-details-title">
                  Transaction Details
                </div>
                <div className="txn-details-subtitle">
                  Complete information for this transaction
                </div>
              </div>
              <div className="txn-details-date">
                <span>Date & Time</span>
                <strong>
                  {row.original?.created_at || "—"}
                </strong>
              </div>
            </div>
            <div className="txn-details-grid">
              <div className="txn-detail-item">
                <span className="txn-detail-label">Transaction ID</span>
                <div className="txn-detail-value txn-detail-copy">
                  <ShortTruncateCopy
                    info={row.original?.et_id}
                    showCopy={true}
                  />
                </div>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Internal ID</span>
                <div className="txn-detail-value txn-detail-copy">
                  <ShortTruncateCopy
                    info={row.original?.id}
                    showCopy={true}
                  />
                </div>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Customer ID</span>
                <div className="txn-detail-value txn-detail-copy">
                  {row.original?.customer_name ? (
                    <ShortTruncateCopy
                      info={row.original.customer_name}
                      showCopy={true}
                    />
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Reference ID</span>
                <div className="txn-detail-value txn-detail-copy">
                  {row.original?.reference_id ? (
                    <ShortTruncateCopy
                      info={row.original.reference_id}
                      showCopy={true}
                    />
                  ) : (
                    "—"
                  )}
                </div>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Descriptor</span>
                <span className="txn-detail-value">
                  {row.original?.meta_info?.Descriptor || "—"}
                </span>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Card Type</span>
                <span className="txn-detail-value txn-card-detail">
                  {row.original?.card_type ? (
                    <>
                      {getCardSVG(row.original.card_type)}
                    </>
                  ) : (
                    "—"
                  )}
                </span>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Currency</span>
                <span className="txn-detail-value">
                  {row.original?.currency || "—"}
                </span>
              </div>
              <div className="txn-detail-item">
                <span className="txn-detail-label">Converted Amount</span>
                <span className="txn-detail-value">
                  {(row.original as any)?.converted_amount != null
                    ? `${Number((row.original as any).converted_amount).toFixed(2)} ${(row.original as any)?.converted_currency || ""}`
                    : "—"}
                </span>
              </div>
            </div>
            <div className="txn-history-section">
              <div className="txn-history-header">
                <div>
                  <div className="txn-history-title">
                    Transaction History
                  </div>

                  <div className="txn-history-subtitle">
                    Complete transaction status timeline
                  </div>
                </div>

                <div className="txn-history-count">
                  {row.original?.payment_history?.length || 0} Events
                </div>
              </div>
              <div className="txn-history-flow">
                {row.original?.payment_history?.length ? (
                  row.original.payment_history.map((h: any, i: number) => {
                    const isLast =
                      i === row.original.payment_history.length - 1;

                    const status = h?.status || "UNKNOWN";

                    const statusClass = status
                      .toLowerCase()
                      .replace(/\s+/g, "-");

                    return (
                      <React.Fragment key={i}>
                        <div className={`txn-history-card ${statusClass}`}>
                          <div className="txn-history-card-top">
                            <span className={`txn-history-status ${statusClass}`}>
                              {status}
                            </span>

                            <span className="txn-history-index">
                              #{i + 1}
                            </span>
                          </div>

                          <div className="txn-history-card-date">
                            {(
                              h?.status_changed_date ||
                              h?.created_at ||
                              "—"
                            ).replace("GMT", "")}
                          </div>

                          {i === 0 ? (
                            <>
                              <div className="txn-history-row">
                                <span>Created By</span>
                                <strong>{h?.client || "API"}</strong>
                              </div>

                              <div className="txn-history-row">
                                <span>IP Address</span>
                                <strong>{h?.ip || "—"}</strong>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="txn-history-row">
                                <span>Previous</span>
                                <strong>{h?.previous_status || "—"}</strong>
                              </div>

                              {h?.reason && (
                                <div className="txn-history-row">
                                  <span>Reason</span>
                                  <strong>{h.reason}</strong>
                                </div>
                              )}

                              {h?.arn && (
                                <div className="txn-history-row">
                                  <span>ARN</span>
                                  <strong>{h.arn}</strong>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {!isLast && (
                          <div className="txn-history-arrow">
                            <span>→</span>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div className="txn-history-empty">
                    No transaction history available.
                  </div>
                )}
              </div>
              <PaymentInformationSection
                paymentResponse={row.original?.meta_info}
                gatewayResponse={row.original?.gateway_response}
                roleData={roleData}
              />
            </div>
          </div>
        </td>
      </tr>
    );
  }
);

const TableRow = React.memo(
  ({ row, cells, isExpanded, onToggle, roleData }: { row: any; cells: any[]; isExpanded: boolean; onToggle: (id: string) => void; roleData:any }) => {
    const id = String(row.original?.id);

    return (
      <>
        <tr
          className={`txn-tbody-tr${isExpanded ? " txn-tbody-tr--expanded" : ""}`}
          onClick={() => onToggle(id)}
        >
          {cells.map((cell: any, ci: number) => (
            <td key={ci} className="txn-tbody-td">
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>

        <TransactionDetailsRow row={row} isExpanded={isExpanded} roleData={roleData}/>
      </>
    );
  }
);

function PaymentTable(props: {
  tableData: any; fetchPayments: () => void; filterData: (f?: any) => void;
  isLoading: boolean; page: number; setPage: any;
  totalpage: number; totalItems: number; currentPage: number;
  clients: any[]; allGateways: any[]; allCardType: any[];
  curretClient: string; setCurrentClient: any;
  pageSize: number; setPageSize: any; roleData: any;
  filter: any; setFilter: (e: any) => void; setClearClick: () => void;
  onValueChange: (value: string) => void;
  setLocalFilters?: (e: any) => void; localFilters?: any; isAllDataReady?: any;searchTexts?:any;
}) {
  const {
    tableData, filterData, fetchPayments, page, clients, allGateways, allCardType,
    setPage, currentPage, totalpage, curretClient, setCurrentClient, totalItems,
    pageSize, setPageSize, roleData, filter, setFilter, setClearClick,
    onValueChange, setLocalFilters, localFilters, isAllDataReady,searchTexts
  } = props;

  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [DownloadLoading, setDownloadLoading] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const { paymentType , loadingTransaction } = useContext(ClientContext);

  const [{ pageIndex }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const location = useLocation();

  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const method = roleData[0]?.payment?.value?.payment_show_method_name;

  /* ── Error extractor for declined statuses ── */
  const extractError = (gatewayResponse: any): string => {
    const errorKeys = [
      "errorMessage",
      "message",
      "decline_reason",
      "error_message",
      "failure_message",
      "failure_code",
      "unified_message",
      "ResponseDescription",
      "description"
    ];

    let lastValue = "";

    const search = (data: any) => {
      if (!data || typeof data !== "object") return;

      for (const key of Object.keys(data)) {
        const value = data[key];

        // Check known error keys
        if (
          errorKeys.includes(key) &&
          key !== "_content" &&
          typeof value === "string" &&
          value.trim()
        ) {
          lastValue = value;
        }

        // Recursively search nested objects/arrays
        if (value && typeof value === "object") {
          search(value);
        }

        // Handle JSON stored inside _content
        if (key === "_content" && typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            search(parsed);
          } catch {
            // Ignore invalid JSON
          }
        }
      }
    };

    search(gatewayResponse);

    return lastValue;
  };

  /* ── Status tooltip content ── */
  const StatusHistoryContent = ({ info }: { info: any }) => {
    const history = info?.row?.original?.payment_history;
    if (!history?.length) return <span className="txn-no-history">No history available.</span>;
    const isDeclined = ["DECLINED","ERRORED","FAILED"].includes(info.getValue() as string);
    const errorMessage = isDeclined ? extractError(info?.row?.original?.gateway_response?.gateway_response) : "";
    return (
      <>
        {history.map((h: any, i: number) => (
          <div key={i} className="txn-history-item">
            {i === 0 ? (
              <p className="txn-history-txt">
                Created at{" "}
                <strong>
                  {h?.created_at?.replace("GMT", "") || "—"}
                </strong>{" "}
                by API call from [{h?.client || "—"} — {h?.ip || "—"}] with status{" "}
                <strong>{h?.status || "—"}</strong>

                {isDeclined && (
                  <>
                    <br />
                    Reason: {errorMessage || "Not mentioned by gateway"}
                  </>
                )}
              </p>
            ) : h?.status === "CHARGEBACK" ? (
              <div className="txn-history-txt">
                <p>
                  Status: {h?.previous_status || "—"} →{" "}
                  <strong>{h?.status || "—"}</strong> at{" "}
                  {h?.status_changed_date?.replace("GMT", "") || "—"}
                </p>

                <p>ARN: {h?.arn || "—"}</p>

                <p>Reason: {h?.reason || "—"}</p>
              </div>
            ) : (
              <p className="txn-history-txt">
                Status: {h?.previous_status || "—"} →{" "}
                <strong>{h?.status || "—"}</strong> at{" "}
                {h?.status_changed_date?.replace("GMT", "") || "—"}

                {isDeclined && (
                  <>
                    <br />
                    Reason: {errorMessage || "Not mentioned by gateway"}
                  </>
                )}
              </p>
            )}
          </div>
        ))}
      </>
    );
  };
  const columns = React.useMemo(() => [
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: () => <span className="txn-th">Date</span>,
      cell: (info) => <span className="txn-date">{info.getValue()}</span>,
      sortingFn: (a, b) =>
        new Date(a.original.created_at).getTime() - new Date(b.original.created_at).getTime(),
      enableSorting: true,
    }),
    columnHelper.accessor("client", {
      id: "client",
      header: () => <span className="txn-th">Client</span>,
      cell: (info: any) => (
        <div className="txn-client-cell">
          <span className="txn-client-name">{info.getValue() || "—"}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.client || "";
        const b = rowB.original.client || "";
        const result = a.localeCompare(b);
        if (result !== 0) return result;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("et_id", {
      id: "et_id",
      header: () => <span className="txn-th">Transaction ID</span>,
      cell: (info) => <ShortTruncateCopy info={info?.getValue()} showCopy={true} />,
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="txn-th">Internal ID</span>,
      cell: (info: any) => <ShortTruncateCopy info={info?.getValue()} showCopy={true} />,
    }),
    columnHelper.accessor("reference_id", {
      id: "reference_id",
      header: () => <span className="txn-th">Reference ID</span>,
      cell: (info) => {
        const gName = info?.getValue();
        return gName ? (
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        ) : (
          "-"
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.reference_id || "";
        const b = rowB.original.reference_id || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: () => <span className="txn-th">Amount</span>,
      cell: (info: any) => (
        <div className="txn-amount-cell">
          <div className="txn-amount-main">
            <span className="txn-amount">
              {convertToFloat(info.getValue())} {info?.row?.original?.currency}
            </span>
            {info?.row?.original?.card_type && (
              <span className="txn-card-type">
                {getCardSVG(info.row.original.card_type)}
              </span>
            )}
          </div>
          {info?.row?.original?.converted_amount &&
            info?.row?.original?.converted_currency && (
              <span className="txn-amount-conv">
                ({Number(info.row.original.converted_amount).toFixed(2)}{" "}
                {info.row.original.converted_currency})
              </span>
            )}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.amount || 0);
        const b = Number(rowB.original.amount || 0);
        if (a !== b) return a - b;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("gateway_id", {
      id: "gateway_id",
      header: () => <span className="txn-th">Gateway Name</span>,
      cell: (info) => {
        const gName = info?.cell?.row?.original?.gateway_name;
        return gName ? (
          <div className="txn-gateway-cell">
            <img
              style={{ height: "auto", width: "16px" }}
              src={getImage(gName)}
              alt={gName}
            />
            <span className="txn-gateway-name">{gName}</span>
          </div>
        ) : (
          <ShortTruncateCopy
            info={info?.cell?.row?.original?.gateway_id || "—"}
            showCopy={true}
          />
        );
      },
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.gateway_id || "";
        const b = rowB.original.gateway_id || "";
        const result = a.localeCompare(b);
        if (result !== 0) return result;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("meta_info.Descriptor", {
      id: "descriptor",
      header: () => <span className="txn-th">Descriptor</span>,
      cell: (info: any) => <span className="txn-descriptor">{info.row.original.meta_info?.Descriptor || "—"}</span>,
      enableSorting: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.meta_info?.Descriptor || "";
        const b = rowB.original.meta_info?.Descriptor || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <span className="txn-th">Status</span>,
      cell: (info) => (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <CustomToolTip
            content={<StatusHistoryContent info={info} />}
            trigger={
              <button className="txn-status-trigger" >
                <StatusRender
                  status={info?.row?.original?.status}
                  value={info.getValue()}
                  
                />
              </button>
            }
            header="Status History"
          />
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.status || "";
        const b = rowB.original.status || "";
        const result = a.localeCompare(b);
        if (result !== 0) return result;
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => <span className="txn-th">Actions</span>,
      cell: (info: any) => {
        const enRefund = allGateways?.find(
          (g) => g.id === info?.row?.original?.gateway_id
        );

        return (
          <>
            <div className="txn-actions">
              <Menu placement="bottom-end">
                <MenuButton as={IconButton} aria-label="Transaction actions" icon={<BsThreeDotsVertical size={16} />} variant="ghost" className="txn-action-menu-btn" onClick={(e) => e.stopPropagation()}/>
                <MenuList minW="190px" p="6px" bg="#ffffff" background="#ffffff" opacity={1} border="1px solid #e2e8f0" borderRadius="12px"  boxShadow="0 10px 30px rgba(15,23,42,0.12)" zIndex={9999} onClick={(e) => e.stopPropagation()}>
                  {roleData?.[0]?.payment?.value?.refund_payment_list && (
                    <MenuItem className="txn-menu-item">
                      <RefundModal
                        info={info.row.original}
                        fetchPayments={fetchPayments}
                        enRefund={enRefund?.refund}
                        transactionID={info.row.original.id}
                        client_is_deleted={info.row.original.client_is_deleted}
                      />
                    </MenuItem>
                  )}

                  {(roleData?.[0]?.payment?.value?.view_payment_response_information ||
                    roleData?.[0]?.payment?.value?.view_gateway_response_information) && (
                    <MenuItem className="txn-menu-item">
                      <InfoModal
                        info={info.row.original}
                        client_is_deleted={info.row.original.client_is_deleted}
                      />
                    </MenuItem>
                  )}

                  {roleData?.[0]?.payment?.value?.view_payment_receipt && (
                    <MenuItem className="txn-menu-item" as="a" href={info.row.original?.receipt_url || undefined} target="_blank" rel="noreferrer" isDisabled={!info.row.original?.receipt_url} onClick={(e: any) => e.currentTarget.blur()} >
                      <span className="inline-flex">
                        <button className="txn-action-btn" disabled={!info.row.original?.receipt_url}>
                          <BsReceipt size={14} />
                          <span>View Receipt</span>
                        </button>
                      </span>
                    </MenuItem>
                  )}

                  {roleData?.[0]?.payment?.value?.retry_payment_list && (
                    <MenuItem className="txn-menu-item">
                      <RetryModal
                        info={info.row.original}
                        fetchPayments={fetchPayments}
                        enRefund={enRefund?.refund}
                        transactionID={info?.row?.original?.id}
                        client_is_deleted={info.row.original.client_is_deleted}
                      />
                    </MenuItem>
                  )}

                  {roleData?.[0]?.payment?.value?.edit_payment_status && (
                    <MenuItem className="txn-menu-item">
                      <ChangeStatusModal
                        info={info.row.original}
                        fetchPayments={fetchPayments}
                        enRefund={enRefund?.refund}
                        transactionID={info?.row?.original?.id}
                        filterData={filterData}
                        client_is_deleted={info.row.original.client_is_deleted}
                      />
                    </MenuItem>
                  )}
                </MenuList>
              </Menu>
            </div>
          </>
          
        );
      },
      enableSorting: false,
    }),
  ], [allGateways, roleData, fetchPayments, filterData]);

  const hasActions = roleData?.[0]?.payment?.value?.view_payment_receipt || roleData?.[0]?.payment?.value?.retry_payment_list || roleData?.[0]?.payment?.value?.edit_payment_status || roleData?.[0]?.payment?.value?.view_payment_response_information || roleData?.[0]?.payment?.value?.view_gateway_response_information || roleData?.[0]?.payment?.value?.refund_payment_list;

  const tableColumns = React.useMemo(
    () => (hasActions ? columns : columns.filter((c: any) => c.id !== "actions")),
    [hasActions, columns]
  );
  
  const table = useReactTable({
    data,
    columns: tableColumns,
    state: { pagination, sorting },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const handelOnDownloadCSV = () => {
    setDownloadLoading(true);
    DownloadCSV(filter?.status, filter?.client, filter?.gateway, filter?.payment_status, filter?.dates?.[0], filter?.dates?.[1],searchTexts)
      .then((csvText) => {
        const blob = new Blob([csvText], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        // link.download = "transactions.csv";
        const now = new Date();
        const timestamp =
          `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_` +
          `${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}-${String(now.getSeconds()).padStart(2, "0")}`;
        link.download = `transactions_${timestamp}.csv`;
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        toast.success("Download complete");
        setTimeout(() => setDownloadLoading(false), 2000);
      })
      .catch((err) => { toast.error(err?.response?.data?.message ?? "Download failed"); setTimeout(() => setDownloadLoading(false), 2000); });
  };

  const labels = {
    left: { title: "All", value: "left" },
    right: { title: "Live", value: "right" },
    center: { title: "Test", value: "center" },
  };

  const onChange = (value: any) => {
    if (value === "left") setFilter({ ...filter, status: "all" });
    else if (value === "right") setFilter({ ...filter, status: "live" });
    else if (value === "center") setFilter({ ...filter, status: "test" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleValueChange = (e: any) => { setExpandedRowId(null);  onValueChange(e); };

  const resetRow = () => {
    setExpandedRowId(null);
  }

  const handleToggleRow = React.useCallback((id: string) => {
    setExpandedRowId((prev) => (prev === id ? null : id));
  }, []);

  React.useEffect(() => { setExpandedRowId(null);  }, [page, pageSize]);

  return (
    <div className="txn-card">
      {/* ── Header ── */}
      <div className="txn-header">
        <div className="txn-header-left">
          <div className="txn-header-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
              <polyline points="17 6 23 6 23 12"/>
            </svg>
          </div>
          <div>
            <h3 className="txn-title">All Transactions</h3>
            <p className="txn-subtitle">{totalItems > 0 ? `${totalItems.toLocaleString()} total transaction${totalItems !== 1 ? "s" : ""}` : "No transactions"}</p>
          </div>
        </div>
        <div className="txn-header-right">
          <div className="nb-toggle-wrap">
            <TripleToggleSwitch
              labels={labels}
              onChange={onChange}
              key={location.pathname}
              loadingTransaction={loadingTransaction}
            />
          </div>
          <button
            className="txn-download-btn"
            onClick={handelOnDownloadCSV}
            disabled={DownloadLoading || table.getRowModel().rows.length === 0}
          >
            {DownloadLoading ? <span className="txn-btn-spinner" /> : <FaDownload size={12} />}
            <span>Export CSV</span>
          </button>
          <FilterModal
            clients={clients} allGateways={allGateways} allCardType={allCardType}
            paymentType={paymentType} curretClient={curretClient}
            setPage={setPage} page={page} setCurrentClient={setCurrentClient}
            filter={filter} setFilter={setFilter} setClearClick={setClearClick}
            filterData={filterData} roleData={roleData}
            setLocalFilters={setLocalFilters} localFilters={localFilters}
            isAllDataReady={isAllDataReady} setPageSize={setPageSize} resetRow= {resetRow}
          /> 
          <Searchbox onSearch={handleValueChange} />
        </div>
      </div>

      {/* ── Table ── */}
      <div className="txn-table-wrap">
        {props.isLoading ? (
          <div className="txn-loading">
            <div className="txn-spinner" />
            <p className="txn-loading-txt">Loading transactions…</p>
          </div>
        ) : (
          <table className="txn-table">
            <thead>
              {table.getHeaderGroups().map((hg, i) => (
                <tr key={i}>
                  {hg.headers.map((h, ci) => (
                    <th key={ci} colSpan={h.colSpan}
                      onClick={h.column.getToggleSortingHandler()}
                      className={`txn-thead-th${h.column.getCanSort() ? " txn-thead-th--sort" : ""}`}
                    >
                      <div className="txn-th-inner">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {h.column.getCanSort() && (
                          <span className="txn-sort-icon">
                            {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> :
                             h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> :
                             <RiArrowUpDownFill size={11} />}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    row={row}
                    cells={row.getVisibleCells()}
                    isExpanded={expandedRowId === String(row.original?.id)}
                    onToggle={handleToggleRow}
                    roleData={roleData}
                  />
                ))
              ) : (
                <tr><td colSpan={12}>
                  <div className="txn-empty">
                    <div className="txn-empty-icon">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
                      </svg>
                    </div>
                    <p className="txn-empty-title">No transactions found</p>
                    <p className="txn-empty-sub">Try adjusting your filters or date range</p>
                  </div>
                </td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      <div className="txn-pagination-wrap">
        <Pagination setPage={setPage} page={page} totalpage={totalpage} currentPage={currentPage} pageSize={pageSize} setPageSize={setPageSize} arraySize={[50, 100, 200]} />
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .nb-toggle-wrap { display: flex; align-items: center; }

        .txn-card { font-family:'Plus Jakarta Sans',sans-serif; background:#fff; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,0.06); }

        .clt-expand-cell { display: flex; align-items: center; gap: 7px; font-size: 11px; font-weight: 600; color: #0f172a; white-space: nowrap; }
        .clt-expand-cell--sm { gap: 6px; font-size: 12.5px; }
        .clt-expand-icon { width: 18px; height: 18px; color: #94a3b8; flex-shrink: 0; transition: color .15s; }
        .clt-expand-icon--open { color: #16a34a; }
        .clt-expand-cell:hover .clt-expand-icon { color: #16a34a; }
        .cus-font-size{font-size:12px}
        .txn-header { padding:20px 24px 16px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; justify-content:space-between; gap:14px; flex-wrap:wrap; }
        .txn-header-left { display:flex; align-items:center; gap:12px; }
        .txn-header-icon { width:40px; height:40px; border-radius:11px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .txn-title { font-size:15px; font-weight:700; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:22px; }
        .txn-subtitle { font-size:12px; color:#94a3b8; font-weight:500; }
        .txn-header-right { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }

        .txn-download-btn { display:inline-flex; align-items:center; gap:6px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; color:#fff; padding:9px 16px; border-radius:10px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 3px 10px rgba(37,99,235,.24); transition:all .15s; white-space:nowrap; }
        .txn-download-btn:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 6px 18px rgba(37,99,235,.32); }
        .txn-download-btn:disabled { opacity:.5; cursor:not-allowed; transform:none; }
        .txn-btn-spinner { width:13px; height:13px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:txnSpin .65s linear infinite; }
        @keyframes txnSpin { to{transform:rotate(360deg)} }

        .txn-table-wrap { overflow-x:auto; width:100%; }
        .txn-table-wrap::-webkit-scrollbar { height:4px; }
        .txn-table-wrap::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }
        .txn-table { width:100%; border-collapse:separate; border-spacing:0; table-layout:auto; }

        .txn-thead-th { padding:11px 12px 9px; background:#f8fafc; border-bottom:1.5px solid #e2e8f0; text-align:left; white-space:nowrap; user-select:none; }
        .txn-thead-th--sort { cursor:pointer; }
        .txn-thead-th--sort:hover { background:#f1f5f9; }
        .txn-th-inner { display:flex; align-items:center; gap:5px; }
        .txn-th { font-size:11px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.5px; }
        .txn-sort-icon { color:#94a3b8; display:flex; align-items:center; }

        .txn-tbody-tr { transition:background .12s;cursor:pointer }
        .txn-tbody-tr:hover { background:#f8fafc; }
        .txn-tbody-tr--expanded { background:#f8fbff; }
        .txn-tbody-td { padding:11px 12px; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
        .txn-tbody-td:first-child { width:42px; padding-left:16px; padding-right:4px; }
        .txn-expand-btn {
          margin-top: -3px;width:26px; height:26px; border-radius:7px; border:1px solid #dbe3ef;
          background:#fff; color:#475569; display:flex; align-items:center;
          justify-content:center; font-size:17px; line-height:1; font-weight:600;
          cursor:pointer; transition:all .15s; padding:0;
        }
        .txn-expand-btn:hover, .txn-expand-btn.is-expanded {
          background:#eff6ff; border-color:#93c5fd; color:#2563eb;
        }

        .txn-details-tr { background:#f8fbff; }
        .txn-details-td { padding:14px 14px 14px 14px; border-bottom:1px solid #e2e8f0; }
        .txn-details-panel {
          border:1px solid #dbe7f5; border-radius:12px; background:#fff;
          box-shadow:0 2px 8px rgba(15,23,42,.04); overflow:hidden;
        }
        .txn-details-header {
          display:flex; align-items:center; justify-content:space-between; gap:20px;
          padding:14px 16px; border-bottom:1px solid #edf2f7;
          background:linear-gradient(180deg,#f8fbff,#fff);
        }
        .txn-details-title { font-size:13px; font-weight:800; color:#0f172a; }
        .txn-details-subtitle { margin-top:2px; font-size:11px; color:#94a3b8; font-weight:500; }
        .txn-details-date { text-align:right; display:flex; flex-direction:column; gap:2px; }
        .txn-details-date span { font-size:10px; color:#94a3b8; font-weight:600; text-transform:uppercase; letter-spacing:.4px; }
        .txn-details-date strong { font-size:11.5px; color:#334155; font-weight:700; }
        .txn-details-grid {
          display:grid; grid-template-columns:repeat(4,minmax(0,1fr));
          gap:0;
        }
        .txn-detail-item {
          min-width:0; padding:12px 15px; border-right:1px solid #edf2f7;
          border-bottom:1px solid #edf2f7; display:flex; flex-direction:column; gap:5px;
        }
        .txn-detail-item:nth-child(4n) { border-right:none; }
        .txn-detail-label {
          font-size:10px; color:#94a3b8; font-weight:700; text-transform:uppercase;
          letter-spacing:.45px;
        }
        .txn-detail-value {
          min-width:0; font-size:12px; color:#334155; font-weight:600;
          overflow:hidden; text-overflow:ellipsis;
        }
        .txn-detail-copy { display:block; }
        .txn-card-detail, .txn-gateway-detail { display:flex; align-items:center; gap:7px; }
        .txn-card-detail img { max-width:48px; height:auto; }
        .txn-gateway-detail img { width:17px; height:17px; object-fit:contain; }
        .txn-detail-history { padding:14px 15px 16px; }
        .txn-detail-section-title {
          font-size:11px; font-weight:800; color:#334155; margin-bottom:8px;
          text-transform:uppercase; letter-spacing:.45px;
        }
        .txn-detail-history-box {
          border:1px solid #edf2f7; border-radius:9px; padding:0 12px;
          background:#fbfdff; max-height:220px; overflow:auto;
        }

        .txn-history-section {
          margin-top: 16px;
          padding: 16px;
          border-top: 1px solid #edf2f7;
          background: #fbfcfe;
        }

        .txn-history-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }

        .txn-history-title {
          font-size: 13px;
          font-weight: 700;
          color: #0f172a;
        }

        .txn-history-subtitle {
          margin-top: 2px;
          font-size: 11px;
          font-weight: 500;
          color: #94a3b8;
        }

        .txn-history-count {
          padding: 4px 9px;
          border-radius: 999px;
          background: #eff6ff;
          border: 1px solid #dbeafe;
          color: #2563eb;
          font-size: 10px;
          font-weight: 700;
        }

        .txn-history-flow {
          display: flex;
          align-items: stretch;
          gap: 10px;
          width: 100%;
          overflow-x: auto;
          padding: 4px 2px 8px;
          scrollbar-width: thin;
        }

        .txn-history-card {
          position: relative;
          flex: 0 0 235px;
          min-height: 145px;
          padding: 13px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 11px;
          box-shadow: 0 2px 7px rgba(15, 23, 42, 0.04);
        }

        .txn-history-card:hover {
          border-color: #bfdbfe;
          box-shadow: 0 5px 16px rgba(37, 99, 235, 0.08);
          transform: translateY(-1px);
          transition: all 0.15s ease;
        }

        .txn-history-card-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .txn-history-status {
          display: inline-flex;
          align-items: center;
          padding: 4px 8px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.2px;
          background: #f1f5f9;
          color: #475569;
        }

        .txn-history-status.approved,
        .txn-history-status.success,
        .txn-history-status.completed {
          background: #ecfdf5;
          color: #059669;
        }

        .txn-history-status.pending,
        .txn-history-status.processing {
          background: #fff7ed;
          color: #d97706;
        }

        .txn-history-status.errored,
        .txn-history-status.failed,
        .txn-history-status.declined {
          background: #fef2f2;
          color: #dc2626;
        }

        .txn-history-status.chargeback {
          background: #fff7ed;
          color: #ea580c;
        }

        .txn-history-index {
          font-size: 10px;
          font-weight: 700;
          color: #cbd5e1;
        }

        .txn-history-card-date {
          margin-top: 10px;
          padding-bottom: 9px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 10.5px;
          color: #64748b;
          white-space: nowrap;
        }

        .txn-history-row {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin-top: 8px;
          font-size: 10.5px;
        }

        .txn-history-row span {
          color: #94a3b8;
          flex-shrink: 0;
        }

        .txn-history-row strong {
          max-width: 135px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #334155;
          font-weight: 600;
          text-align: right;
        }

        .txn-history-arrow {
          flex: 0 0 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-size: 20px;
          font-weight: 700;
        }

        .txn-history-arrow span {
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          font-size: 14px;
        }

        .txn-history-empty {
          width: 100%;
          padding: 25px;
          text-align: center;
          color: #94a3b8;
          font-size: 12px;
        }

        /* Cell styles */
        .txn-client-cell {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          max-width: 120px;
        }

        .txn-client-avatar {
          width: 22px;
          height: 22px;
          min-width: 22px;
          border-radius: 6px;

          display: inline-flex;
          align-items: center;
          justify-content: center;

          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #2563eb;

          font-size: 10px;
          font-weight: 700;
          line-height: 1;
        }

        .txn-client-name {
          min-width: 0;

          font-size: 12px;
          font-weight: 600;
          color: #1e293b;

          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .txn-date { font-size:11px; font-weight:600; color:#0f172a; white-space:nowrap; }
        .txn-amount-cell { display:flex; flex-direction: column; gap:2px; }
        .txn-amount-main {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
          white-space: nowrap;
        }
        .txn-card-type img {
          display: block;
          max-width: 42px !important;
          max-height: 20px !important;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        .txn-amount-conv {
          font-size: 11px;
          color: #94a3b8;
          font-weight: 500;
          white-space: nowrap;
        }

        .txn-payment-info-section {
          margin-top: 18px;
          padding-top: 18px;
          border-top: 1px solid #e8edf3;
        }

        .txn-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 14px;
        }

        .txn-section-heading {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .txn-section-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .txn-section-icon--blue {
          background: #eff6ff;
          border: 1px solid #dbeafe;
          color: #2563eb;
        }

        .txn-section-icon--green {
          background: #ecfdf5;
          border: 1px solid #d1fae5;
          color: #059669;
        }

        .txn-section-title {
          font-size: 13px;
          font-weight: 800;
          color: #0f172a;
        }

        .txn-section-subtitle {
          margin-top: 2px;
          font-size: 10.5px;
          color: #94a3b8;
          font-weight: 500;
        }

        .txn-payment-summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          overflow: hidden;
          background: #fff;
        }

        .txn-payment-field {
          min-height: 66px;
          padding: 11px 13px;
          border-right: 1px solid #edf2f7;
          border-bottom: 1px solid #edf2f7;
        }

        .txn-payment-field-label {
          display: block;
          margin-bottom: 6px;
          color: #94a3b8;
          font-size: 9.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .35px;
        }

        .txn-payment-field-value {
          display: block;
          color: #1e293b;
          font-size: 11.5px;
          font-weight: 650;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .txn-payment-amount {
          color: #0f172a;
          font-size: 13px;
          font-weight: 800;
        }

        .txn-payment-status {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 7px;
          border-radius: 5px;
          font-size: 9.5px;
          font-weight: 800;
        }

        .txn-payment-status-dot {
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
        }

        .txn-payment-status.approved,
        .txn-payment-status.success,
        .txn-payment-status.completed {
          color: #059669;
          background: #ecfdf5;
        }

        .txn-payment-status.pending,
        .txn-payment-status.processing {
          color: #d97706;
          background: #fff7ed;
        }

        .txn-payment-status.failed,
        .txn-payment-status.errored,
        .txn-payment-status.declined {
          color: #dc2626;
          background: #fef2f2;
        }

        .txn-info-subsection {
          margin-top: 14px;
        }

        .txn-info-subsection-title {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
          color: #475569;
          font-size: 10.5px;
          font-weight: 800;
        }

        .txn-info-card-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }

        .txn-info-card {
          padding: 10px 12px;
          border: 1px solid #e5eaf0;
          border-radius: 8px;
          background: #ffffff;
        }

        .txn-info-card span,
        .txn-description-card span,
        .txn-dynamic-field span {
          display: block;
          margin-bottom: 5px;
          font-size: 9.5px;
          font-weight: 650;
        }

        .txn-info-card strong,
        .txn-description-card strong,
        .txn-dynamic-field strong {
          display: block;
          color: #334155;
          font-size: 11px;
          font-weight: 650;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .txn-info-card--wide {
          grid-column: span 1;
        }

        .txn-dynamic-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
        }

        .txn-dynamic-field {
          min-width: 0;
          padding: 10px 12px;
          background: #f8fafc;
          border: 1px solid #e8edf3;
          border-radius: 8px;
        }

        .txn-description-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
        }

        .txn-description-card {
          min-width: 0;
          padding: 10px 12px;
          background: #fff;
          border: 1px solid #e5eaf0;
          border-radius: 8px;
        }

        .txn-description-card--full {
          grid-column: 1 / -1;
        }

        .txn-gateway-section {
          margin-top: 20px;
          padding-top: 18px;
          border-top: 1px solid #e8edf3;
        }

        .txn-gateway-flow {
          display: flex;
          align-items: stretch;
          gap: 9px;
          overflow-x: auto;
          padding: 3px 2px 8px;
        }

        .txn-gateway-card {
          flex: 1 1 0;
          min-width: 285px;
          border: 1px solid #e2e8f0;
          border-radius: 11px;
          background: #fff;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.035);
        }

        .txn-gateway-card:hover {
          border-color: #cbd5e1;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.07);
        }

        .txn-gateway-card-header {
          padding: 11px 13px;
          border-bottom: 1px solid #edf2f7;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .txn-gateway-card-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .txn-gateway-card-title strong {
          display: block;
          color: #1e293b;
          font-size: 11px;
          font-weight: 800;
        }

        .txn-gateway-card-title span {
          display: block;
          margin-top: 1px;
          color: #94a3b8;
          font-size: 9px;
        }

        .txn-gateway-card-icon {
          width: 27px;
          height: 27px;
          border-radius: 7px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #eff6ff;
          color: #2563eb;
        }

        .txn-gateway-card--response .txn-gateway-card-icon {
          background: #ecfdf5;
          color: #059669;
        }

        .txn-gateway-card--requested .txn-gateway-card-icon {
          background: #f5f3ff;
          color: #7c3aed;
        }

        .txn-gateway-card-number {
          color: #cbd5e1;
          font-size: 10px;
          font-weight: 800;
        }

        .txn-gateway-card-body {
          padding: 10px 12px;
          max-height: 330px;
          overflow-y: auto;
        }

        .txn-json-list {
          display: flex;
          flex-direction: column;
          gap: 7px;
        }

        .txn-json-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-height: 27px;
          padding-bottom: 6px;
          border-bottom: 1px dashed #edf2f7;
        }

        .txn-json-item:last-child {
          border-bottom: 0;
        }

        .txn-json-label {
          color: #94a3b8;
          font-size: 9.5px;
          font-weight: 650;
          flex-shrink: 0;
        }

        .txn-json-value {
          max-width: 190px;
          color: #334155;
          font-size: 10px;
          font-weight: 650;
          text-align: right;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .txn-json-item--nested {
          display: block;
          padding: 8px;
          border: 1px solid #edf2f7;
          border-radius: 7px;
          background: #fafbfc;
        }

        .txn-json-item--nested > .txn-json-label {
          display: block;
          margin-bottom: 7px;
          color: #64748b;
          font-weight: 800;
        }

        .txn-json-nested {
          padding-left: 7px;
          border-left: 2px solid #e2e8f0;
        }

        .txn-json-empty {
          padding: 20px;
          text-align: center;
          color: #94a3b8;
          font-size: 10px;
        }

        @media (max-width: 900px) {
          .txn-payment-summary-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .txn-info-card-grid,
          .txn-dynamic-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .txn-gateway-card {
            min-width: 270px;
          }
        }

        @media (max-width: 600px) {
          .txn-payment-summary-grid,
          .txn-info-card-grid,
          .txn-dynamic-grid,
          .txn-description-grid {
            grid-template-columns: 1fr;
          }

          .txn-description-card--full {
            grid-column: auto;
          }
        }

        .txn-copy-url {
          display: flex;
          align-items: center;
          gap: 7px;
          width: 100%;
          min-width: 0;
        }

        .txn-copy-url-text {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          color: #334155;
          font-size: 10.5px;
          font-weight: 600;
        }

        .txn-copy-url-btn {
          width: 26px;
          height: 26px;
          flex: 0 0 26px;

          display: flex;
          align-items: center;
          justify-content: center;

          border: 1px solid #e2e8f0;
          border-radius: 6px;
          background: #ffffff;
          color: #64748b;

          cursor: pointer;
          transition: all 0.15s ease;
        }

        .txn-copy-url-btn:hover {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #2563eb;
        }

        .txn-copy-url-btn.copied {
          background: #ecfdf5;
          border-color: #a7f3d0;
          color: #059669;
        }

        .txn-url-empty {
          color: #94a3b8;
          font-size: 10.5px;
        }

        .txn-gateway-url {
          margin-bottom: 10px;
          padding: 9px 10px;

          border: 1px solid #e2e8f0;
          border-radius: 8px;
          background: #f8fafc;
        }

        .txn-gateway-url > span {
          display: block;
          margin-bottom: 6px;

          color: #94a3b8;
          font-size: 9px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: .3px;
        }
        .txn-amount { font-size:12px; font-weight:700; color:#0f172a; white-space:nowrap; }
        .txn-amount-conv { font-size:11px; color:#94a3b8; font-weight:500; }
        .txn-gateway-cell { display:flex; align-items:center; gap:6px; }
        .txn-gateway-name { font-size:12.5px; font-weight:600; color:#0f172a; }
        .txn-descriptor { font-size:12.5px; font-weight:500; color:#475569; }
        .txn-muted { font-size:12.5px; color:#cbd5e1; font-weight:500; }
        .txn-status-trigger { background:none; border:none; cursor:pointer; display:flex; align-items:center; }
        .txn-actions { display:flex; align-items:center; gap:5px; }
        .txn-action-btn {
          min-width: 28px;
          min-height: 28px;
          width: auto;
          height: 28px;
          padding: 0 9px;
          color: #64748b;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          transition: all .15s;
          text-decoration: none;
          cursor: pointer;
          white-space: nowrap;
        }

        .txn-action-btn:hover {
          background: #eff6ff;
          border-color: #bfdbfe;
          color: #2563eb;
        }

        .txn-action-btn svg {
          flex-shrink: 0;
        }

        /* =========================================
          Premium Thin Scrollbar
          ========================================= */

        .txn-payment-info-section *,
        .txn-gateway-section *,
        .txn-gateway-card-body,
        .txn-history-flow {
          scrollbar-width: thin;
          scrollbar-color: #d8dee8 transparent;
        }

        /* Chrome / Edge / Safari */
        .txn-payment-info-section *::-webkit-scrollbar,
        .txn-gateway-section *::-webkit-scrollbar,
        .txn-history-flow::-webkit-scrollbar {
          width: 4px;
          height: 4px;
        }

        .txn-payment-info-section *::-webkit-scrollbar-track,
        .txn-gateway-section *::-webkit-scrollbar-track,
        .txn-history-flow::-webkit-scrollbar-track {
          background: transparent;
        }

        .txn-payment-info-section *::-webkit-scrollbar-thumb,
        .txn-gateway-section *::-webkit-scrollbar-thumb,
        .txn-history-flow::-webkit-scrollbar-thumb {
          background: #d8dee8;
          border-radius: 999px;
        }

        .txn-payment-info-section *::-webkit-scrollbar-thumb:hover,
        .txn-gateway-section *::-webkit-scrollbar-thumb:hover,
        .txn-history-flow::-webkit-scrollbar-thumb:hover {
          background: #c4ccd8;
        }

        /* History tooltip */
        .txn-history-item { padding:8px 0; border-bottom:1px solid #f1f5f9; }
        .txn-history-item:last-child { border-bottom:none; }
        .txn-history-txt { font-size:12px; color:#374151; line-height:1.5; }
        .txn-no-history { font-size:12px; color:#94a3b8; font-style:italic; }

        /* Loading / empty */
        .txn-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px; gap:12px; }
        .txn-spinner { width:28px; height:28px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:txnSpin .7s linear infinite; }
        .txn-loading-txt { font-size:13px; color:#94a3b8; font-weight:500; }
        .txn-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:56px 24px; gap:10px; }
        .txn-empty-icon { width:52px; height:52px; border-radius:13px; background:#f8fafc; border:1.5px solid #e2e8f0; display:flex; align-items:center; justify-content:center; color:#cbd5e1; }
        .txn-empty-title { font-size:14px; font-weight:700; color:#64748b; }
        .txn-empty-sub { font-size:12.5px; color:#94a3b8; font-weight:500; text-align:center; }

        .txn-pagination-wrap { padding:12px 16px; border-top:1px solid #f1f5f9; background:#fafbfc; }
        .txn-actions {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .txn-action-menu-btn {
          width: 30px !important;
          height: 30px !important;
          min-width: 30px !important;
          border-radius: 7px !important;
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          color: #64748b !important;
          padding: 0 !important;
        }

        .txn-action-menu-btn:hover {
          background: #eff6ff !important;
          border-color: #bfdbfe !important;
          color: #2563eb !important;
        }

        .txn-menu-item {
          background: #ffffff !important;
          display: flex !important;
          align-items: center !important;
          gap: 8px !important;
          min-height: 34px !important;
          padding: 7px 9px !important;
          border-radius: 7px !important;
          font-family: 'Plus Jakarta Sans', sans-serif !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          color: #334155 !important;
          white-space: nowrap !important;
        }

        .txn-menu-item:hover {
          background: #f1f5f9 !important;
          color: #2563eb !important;
        }

        .txn-menu-icon {
          width: 18px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          flex-shrink: 0;
        }

        .txn-menu-item:hover .txn-menu-icon {
          color: #2563eb;
        }

        .chakra-menu__menu-list {
          background: #ffffff !important;
          background-color: #ffffff !important;
          opacity: 1 !important;
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
        @media (max-width:1100px) {
          .txn-details-grid { grid-template-columns:repeat(3,minmax(0,1fr)); }
          .txn-detail-item:nth-child(4n) { border-right:1px solid #edf2f7; }
          .txn-detail-item:nth-child(3n) { border-right:none; }
        }
        @media (max-width:850px) {
          .txn-details-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
          .txn-detail-item:nth-child(3n) { border-right:1px solid #edf2f7; }
          .txn-detail-item:nth-child(2n) { border-right:none; }
          .txn-details-td { padding-left:30px; }
          .txn-th { font-size:10px; }
          .txn-tbody-td { padding-left:8px; padding-right:8px; }
        }
        @media (max-width:700px) {
          .txn-header { flex-direction:column; align-items:flex-start; }
          .txn-header-right { width:100%; }
          .txn-table-wrap { overflow-x:auto; }
          .txn-table { min-width:850px; }
          .txn-details-td { padding-left:14px; }
        }
      `}</style>
    </div>
  );
}

export default PaymentTable;